// hyperlane.ts — Client-side reading of the galaxy's hyperlane graph. The generator
// emits a flat list of undirected {a,b} system-id links; here we turn the links
// incident to one system into "gates" (points of singularity) with a direction to
// aim them, a real distance in light-years, and a transit time.
//
// Lore scale: hyperlanes run at 10× lightspeed, and one galaxy-space scene unit is
// LIGHT_YEARS_PER_UNIT light-years — sized so neighbouring systems sit a few ly
// apart (like real nearby stars) and a hop costs months to a few years of game time.
import type { Galaxy, StarSystem } from '../types';

export const HYPERLANE_SPEED_C = 10; // 10× lightspeed
export const LIGHT_YEARS_PER_UNIT = 1.6; // galaxy-space scene units → light-years
const YEAR_SECONDS = 31557600; // 365.25 d — matches SystemPanel's period formatter

export function distanceUnits(a: readonly number[], b: readonly number[]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** A hyperlane as seen from one of its endpoint systems: where it goes and how far. */
export interface Gate {
  /** Stable id `${systemId}>${neighborId}` — used for hover/selection. */
  id: string;
  /** The system this gate leads to. */
  neighbor: StarSystem;
  /** Unit direction from this system toward the neighbor (galaxy space). */
  dir: [number, number, number];
  /** Straight-line distance in scene units. */
  units: number;
  /** Distance in light-years (units × LIGHT_YEARS_PER_UNIT). */
  lightYears: number;
  /** Transit time in game-seconds at HYPERLANE_SPEED_C. */
  transitSeconds: number;
}

/** Build the gate id a Singularity / panel row uses for a (system → neighbor) lane. */
export function gateId(systemId: string, neighborId: string): string {
  return `${systemId}>${neighborId}`;
}

/** Every hyperlane incident to `systemId`, as gates sorted nearest-first. */
export function gatesOf(galaxy: Galaxy, systemId: string): Gate[] {
  const byId = new Map(galaxy.systems.map((s) => [s.id, s]));
  const self = byId.get(systemId);
  if (!self) {
    return [];
  }
  const gates: Gate[] = [];
  for (const h of galaxy.hyperlanes) {
    const otherId = h.a === systemId ? h.b : h.b === systemId ? h.a : null;
    if (!otherId) {
      continue;
    }
    const neighbor = byId.get(otherId);
    if (!neighbor) {
      continue;
    }
    const units = distanceUnits(self.position, neighbor.position) || 1e-6;
    const ly = units * LIGHT_YEARS_PER_UNIT;
    gates.push({
      id: gateId(systemId, otherId),
      neighbor,
      dir: [
        (neighbor.position[0] - self.position[0]) / units,
        (neighbor.position[1] - self.position[1]) / units,
        (neighbor.position[2] - self.position[2]) / units,
      ],
      units,
      lightYears: ly,
      transitSeconds: (ly / HYPERLANE_SPEED_C) * YEAR_SECONDS,
    });
  }
  gates.sort((p, q) => p.units - q.units || (p.id < q.id ? -1 : 1));
  return gates;
}
